import jsonpath from 'jsonpath';
import React from 'react';
import useSWR from 'swr';
import { ChartContainer } from '../../components/chart/ChartContainer';
import { GeneralReportDataGrid } from '../../components/datagrid/GeneralReportDataGrid';
import { useAssetClass } from '../../contexts/assetClass';
import { useAuth } from '../../contexts/auth';
import {
  Dashboard,
  DashboardStyle,
  DashboardType,
  DatagridStyle,
} from '../../types/dashboard.types';
import { fetcher } from '../../utils/apiUrl';

export default function DashboardBlock({ label, type, source, style, jsonPathFilter }: Dashboard) {
  const { assetClass } = useAssetClass();
  const { user } = useAuth();

  let apiUrl = source.replace('{assetClassId}', assetClass.toString());
  if (user) apiUrl = apiUrl.replace('{userId}', user.personalNumber.toString());

  const { data: apiResult, isValidating: apiLoading } = useSWR<number | string>(
    type !== 'Empty' ? apiUrl : null,
    fetcher,
  );

  if (type === 'Empty') return <EmptyResult />;

  return (
    <div style={{ padding: '5px', textAlign: 'center' }}>
      <div className={'dx-card responsive-paddings'}>
        <div style={{ fontSize: '18px', marginBottom: '5px' }}>{label}</div>
        {apiLoading || apiResult === undefined ? (
          <>Loading</>
        ) : (
          <Result apiResult={apiResult} type={type} style={style} jsonPathFilter={jsonPathFilter} />
        )}
      </div>
    </div>
  );
}

const Result = ({
  apiResult,
  type,
  style,
  jsonPathFilter,
}: {
  apiResult: number | string;
  type: DashboardType;
  style: DashboardStyle | DatagridStyle | null;
  jsonPathFilter: string | null;
}) => {
  if (type === 'Number')
    return <NumberResult value={apiResult as number} style={style as DashboardStyle} />;
  if (type === 'Text')
    return <StringResult value={apiResult.toString() as string} style={style as DashboardStyle} />;
  if (type === 'Table') return <TableResult value={apiResult} />;
  if (type === 'Chart') return <ChartResult value={apiResult} />;
  if (type === 'Json' && jsonPathFilter)
    return (
      <JsonResult
        value={apiResult}
        style={style as DashboardStyle}
        jsonPathFilter={jsonPathFilter}
      />
    );

  return <>Unsopported type</>;
};

const EmptyResult = () => {
  return <span></span>;
};

const NumberResult = ({ value, style }: { value: number; style: DashboardStyle | null }) => {
  return <StyledSpan value={value} style={style} />;
};

const StringResult = ({ value, style }: { value: string; style: DashboardStyle | null }) => {
  return <StyledSpan value={value} style={style} />;
};

const TableResult = ({ value }: { value: any }) => {
  const style: DatagridStyle = value.datagrid.components as DatagridStyle;

  return (
    <div style={{ overflowX: 'auto' }}>
      <GeneralReportDataGrid
        selectedFilterDatagrid={null}
        data={null}
        dataAndStructure={value}
        filterVisibility={false}
        setFilterVisibility={() => null}
        transFormName={''}
        newEntryActionPath={''}
        disableCustomToolbar
        disableCustomHeight
        enableGrouping={style && style.enableGrouping !== undefined ? style.enableGrouping : true}
        enableXlsxExport={
          style && style.enableXlsxExport !== undefined ? style.enableXlsxExport : true
        }
        enableColumnChooser={
          style && style.enableColumnChooser !== undefined ? style.enableColumnChooser : true
        }
        enableHeaderFiltering={
          style && style.enableHeaderFiltering !== undefined ? style.enableHeaderFiltering : true
        }
        enableRowFiltering={
          style && style.enableRowFiltering !== undefined ? style.enableRowFiltering : true
        }
        enableSearchPanel={
          style && style.enableSearchPanel !== undefined ? style.enableSearchPanel : true
        }
        enableColumnReordering={
          style && style.enableColumnReordering !== undefined ? style.enableColumnReordering : true
        }
        enableColumnResizing={
          style && style.enableColumnResizing !== undefined ? style.enableColumnResizing : true
        }
        datagridId={`datagrid_report_dashboard`}
      />
    </div>
  );
};

const ChartResult = ({ value }: { value: any }) => {
  return <ChartContainer data={value.data} config={value.chart} />;
};

const JsonResult = ({
  value,
  style,
  jsonPathFilter,
}: {
  value: any;
  style: DashboardStyle | null;
  jsonPathFilter: string;
}) => {
  var result = jsonpath.query(value, jsonPathFilter);
  return <StyledSpan value={result} style={style} />;
};

const StyledSpan = ({ value, style }: { value: string | number; style: DashboardStyle | null }) => {
  return (
    <span
      style={{
        fontWeight: style?.fontWeight || 'normal',
        fontSize: style?.fontSize || 40,
        color: style?.color || 'black',
      }}
    >
      {value}
    </span>
  );
};
